import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  Loader,
  AlertCircle,
  Save,
  Eye,
  EyeOff,
  Image,
  Video,
  Upload,
  X,
  Pencil,
  BookOpen,
  Play,
  Clock,

  Unlock,
} from 'lucide-react';
import { supabase } from '../lib/supabase';

// Cast to any to bypass strict typing for new tables not yet in database.types.ts
const db = supabase as any;

interface MasterclassLesson {
  id: string;
  chapter_id: string;
  title: string;
  description: string | null;
  video_url: string | null;
  thumbnail_url: string | null;
  duration_seconds: number | null;
  sort_order: number;
  is_published: boolean;
  is_free: boolean;
  created_at: string;
  updated_at: string;
}

interface MasterclassChapter {
  id: string;
  title: string;
  description: string | null;
  thumbnail_url: string | null;
  sort_order: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
  lessons: MasterclassLesson[];
}

export const MasterclassAdmin: React.FC = () => {
  const [chapters, setChapters] = useState<MasterclassChapter[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedChapters, setExpandedChapters] = useState<Set<string>>(new Set());
  const [editingChapter, setEditingChapter] = useState<string | null>(null);
  const [editingLesson, setEditingLesson] = useState<string | null>(null);
  const [uploadingThumbnail, setUploadingThumbnail] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const { data: chaptersData, error: chaptersError } = await db
        .from('masterclass_chapters')
        .select('*')
        .order('sort_order', { ascending: true });

      if (chaptersError) throw chaptersError;

      const { data: lessonsData, error: lessonsError } = await db
        .from('masterclass_lessons')
        .select('*')
        .order('sort_order', { ascending: true });

      if (lessonsError) throw lessonsError;

      const chaptersWithLessons = (chaptersData || []).map((ch: any) => ({
        ...ch,
        lessons: (lessonsData || []).filter((l: any) => l.chapter_id === ch.id),
      }));

      setChapters(chaptersWithLessons);
      // Expand all chapters by default
      setExpandedChapters(new Set((chaptersData || []).map((ch: any) => ch.id)));
    } catch (err: any) {
      console.error('Error fetching masterclass data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleChapter = (id: string) => {
    setExpandedChapters((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const addChapter = async () => {
    try {
      const maxOrder = chapters.reduce((max, ch) => Math.max(max, ch.sort_order), -1);
      const { data, error } = await db
        .from('masterclass_chapters')
        .insert({ title: 'New Chapter', sort_order: maxOrder + 1 })
        .select()
        .single();

      if (error) throw error;
      const newChapter = { ...data, lessons: [] };
      setChapters((prev: MasterclassChapter[]) => [...prev, newChapter]);
      setExpandedChapters((prev: Set<string>) => new Set([...prev, data.id]));
      setEditingChapter(data.id);
    } catch (err: any) {
      console.error('Error adding chapter:', err);
      setError(err.message);
    }
  };

  const updateChapter = async (id: string, updates: Partial<MasterclassChapter>) => {
    try {
      setSaving(id);
      const { error } = await db
        .from('masterclass_chapters')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      setChapters((prev) =>
        prev.map((ch) => (ch.id === id ? { ...ch, ...updates } : ch))
      );
    } catch (err: any) {
      console.error('Error updating chapter:', err);
      setError(err.message);
    } finally {
      setSaving(null);
    }
  };

  const deleteChapter = async (id: string) => {
    if (!confirm('Delete this chapter and all its lessons?')) return;
    try {
      const { error } = await db
        .from('masterclass_chapters')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setChapters((prev) => prev.filter((ch) => ch.id !== id));
    } catch (err: any) {
      console.error('Error deleting chapter:', err);
      setError(err.message);
    }
  };

  const addLesson = async (chapterId: string) => {
    try {
      const chapter = chapters.find((ch) => ch.id === chapterId);
      const maxOrder = (chapter?.lessons || []).reduce(
        (max, l) => Math.max(max, l.sort_order),
        -1
      );

      const { data, error } = await db
        .from('masterclass_lessons')
        .insert({ chapter_id: chapterId, title: 'New Lesson', sort_order: maxOrder + 1 })
        .select()
        .single();

      if (error) throw error;

      setChapters((prev: MasterclassChapter[]) =>
        prev.map((ch: MasterclassChapter) =>
          ch.id === chapterId ? { ...ch, lessons: [...ch.lessons, data as MasterclassLesson] } : ch
        )
      );
      setEditingLesson(data.id);
    } catch (err: any) {
      console.error('Error adding lesson:', err);
      setError(err.message);
    }
  };

  const updateLesson = async (
    chapterId: string,
    lessonId: string,
    updates: Partial<MasterclassLesson>
  ) => {
    try {
      setSaving(lessonId);
      const { error } = await db
        .from('masterclass_lessons')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', lessonId);

      if (error) throw error;

      setChapters((prev) =>
        prev.map((ch) =>
          ch.id === chapterId
            ? {
                ...ch,
                lessons: ch.lessons.map((l) =>
                  l.id === lessonId ? { ...l, ...updates } : l
                ),
              }
            : ch
        )
      );
    } catch (err: any) {
      console.error('Error updating lesson:', err);
      setError(err.message);
    } finally {
      setSaving(null);
    }
  };

  const deleteLesson = async (chapterId: string, lessonId: string) => {
    if (!confirm('Delete this lesson?')) return;
    try {
      const { error } = await db
        .from('masterclass_lessons')
        .delete()
        .eq('id', lessonId);

      if (error) throw error;

      setChapters((prev) =>
        prev.map((ch) =>
          ch.id === chapterId
            ? { ...ch, lessons: ch.lessons.filter((l) => l.id !== lessonId) }
            : ch
        )
      );
    } catch (err: any) {
      console.error('Error deleting lesson:', err);
      setError(err.message);
    }
  };

  const handleThumbnailUpload = async (
    file: File,
    type: 'chapter' | 'lesson',
    id: string,
    chapterId?: string
  ) => {
    try {
      setUploadingThumbnail(id);
      const fileExt = file.name.split('.').pop();
      const fileName = `${type}s/${id}-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('masterclass')
        .upload(fileName, file, { cacheControl: '3600', upsert: true });

      if (uploadError) throw uploadError;

      const {
        data: { publicUrl },
      } = supabase.storage.from('masterclass').getPublicUrl(fileName);

      if (type === 'chapter') {
        await updateChapter(id, { thumbnail_url: publicUrl });
      } else if (chapterId) {
        await updateLesson(chapterId, id, { thumbnail_url: publicUrl });
      }
    } catch (err: any) {
      console.error('Error uploading thumbnail:', err);
      setError(err.message);
    } finally {
      setUploadingThumbnail(null);
    }
  };

  const moveChapter = async (index: number, direction: 'up' | 'down') => {
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= chapters.length) return;

    const updated = [...chapters];
    [updated[index], updated[swapIndex]] = [updated[swapIndex], updated[index]];
    updated.forEach((ch, i) => (ch.sort_order = i));
    setChapters(updated);

    await Promise.all(
      [updated[index], updated[swapIndex]].map((ch) =>
        db
          .from('masterclass_chapters')
          .update({ sort_order: ch.sort_order })
          .eq('id', ch.id)
      )
    );
  };

  const moveLesson = async (
    chapterId: string,
    lessonIndex: number,
    direction: 'up' | 'down'
  ) => {
    const chapter = chapters.find((ch) => ch.id === chapterId);
    if (!chapter) return;

    const swapIndex = direction === 'up' ? lessonIndex - 1 : lessonIndex + 1;
    if (swapIndex < 0 || swapIndex >= chapter.lessons.length) return;

    const updatedLessons = [...chapter.lessons];
    [updatedLessons[lessonIndex], updatedLessons[swapIndex]] = [
      updatedLessons[swapIndex],
      updatedLessons[lessonIndex],
    ];
    updatedLessons.forEach((l, i) => (l.sort_order = i));

    setChapters((prev) =>
      prev.map((ch) =>
        ch.id === chapterId ? { ...ch, lessons: updatedLessons } : ch
      )
    );

    await Promise.all(
      [updatedLessons[lessonIndex], updatedLessons[swapIndex]].map((l) =>
        db
          .from('masterclass_lessons')
          .update({ sort_order: l.sort_order })
          .eq('id', l.id)
      )
    );
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <BookOpen className="h-7 w-7 text-[#0000fe]" />
            Masterclass
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Manage video course chapters and lessons
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={addChapter}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-[#0000fe] text-white rounded-lg hover:bg-[#0000dd] transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Chapter
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <p>{error}</p>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Chapters */}
      {chapters.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p className="text-lg">No chapters yet</p>
          <p className="text-sm mt-1">Click "Add Chapter" to get started</p>
        </div>
      ) : (
        <div className="space-y-4">
          {chapters.map((chapter, chapterIndex) => (
            <div
              key={chapter.id}
              className="bg-dark-800 border border-dark-700 rounded-xl overflow-hidden"
            >
              {/* Chapter Header */}
              <div className="flex items-center gap-3 p-4">
                {/* Reorder */}
                <div className="flex flex-col gap-0.5">
                  <button
                    onClick={() => moveChapter(chapterIndex, 'up')}
                    disabled={chapterIndex === 0}
                    className="text-gray-500 hover:text-white disabled:opacity-20 text-xs"
                    title="Move up"
                  >
                    ▲
                  </button>
                  <button
                    onClick={() => moveChapter(chapterIndex, 'down')}
                    disabled={chapterIndex === chapters.length - 1}
                    className="text-gray-500 hover:text-white disabled:opacity-20 text-xs"
                    title="Move down"
                  >
                    ▼
                  </button>
                </div>

                {/* Expand toggle */}
                <button
                  onClick={() => toggleChapter(chapter.id)}
                  className="text-gray-400 hover:text-white"
                >
                  {expandedChapters.has(chapter.id) ? (
                    <ChevronDown className="h-5 w-5" />
                  ) : (
                    <ChevronRight className="h-5 w-5" />
                  )}
                </button>

                {/* Thumbnail */}
                <div className="relative h-12 w-20 bg-dark-700 rounded-lg overflow-hidden flex-shrink-0 group">
                  {chapter.thumbnail_url ? (
                    <img
                      src={chapter.thumbnail_url}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center">
                      <Image className="h-4 w-4 text-gray-600" />
                    </div>
                  )}
                  <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                    {uploadingThumbnail === chapter.id ? (
                      <Loader className="h-4 w-4 animate-spin text-white" />
                    ) : (
                      <Upload className="h-4 w-4 text-white" />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleThumbnailUpload(file, 'chapter', chapter.id);
                      }}
                    />
                  </label>
                </div>

                {/* Title */}
                <div className="flex-1 min-w-0">
                  {editingChapter === chapter.id ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={chapter.title}
                        onChange={(e) =>
                          setChapters((prev) =>
                            prev.map((ch) =>
                              ch.id === chapter.id
                                ? { ...ch, title: e.target.value }
                                : ch
                            )
                          )
                        }
                        onBlur={() => {
                          updateChapter(chapter.id, { title: chapter.title });
                          setEditingChapter(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            updateChapter(chapter.id, { title: chapter.title });
                            setEditingChapter(null);
                          }
                        }}
                        className="w-full bg-dark-700 text-white text-lg font-semibold px-3 py-1 rounded-md border border-dark-600 focus:border-[#0000fe] focus:outline-none"
                        autoFocus
                      />
                      <textarea
                        value={chapter.description || ''}
                        onChange={(e) =>
                          setChapters((prev) =>
                            prev.map((ch) =>
                              ch.id === chapter.id
                                ? { ...ch, description: e.target.value }
                                : ch
                            )
                          )
                        }
                        onBlur={() =>
                          updateChapter(chapter.id, {
                            description: chapter.description,
                          })
                        }
                        placeholder="Chapter description..."
                        rows={2}
                        className="w-full bg-dark-700 text-gray-300 text-sm px-3 py-1.5 rounded-md border border-dark-600 focus:border-[#0000fe] focus:outline-none resize-none"
                      />
                    </div>
                  ) : (
                    <div>
                      <h3 className="text-lg font-semibold text-white truncate">
                        <span className="text-gray-500 mr-2">
                          {chapterIndex + 1}.
                        </span>
                        {chapter.title}
                      </h3>
                      {chapter.description && (
                        <p className="text-sm text-gray-400 truncate">
                          {chapter.description}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-gray-500">
                    {chapter.lessons.length} lesson
                    {chapter.lessons.length !== 1 ? 's' : ''}
                  </span>

                  {saving === chapter.id && (
                    <Loader className="h-4 w-4 animate-spin text-gray-400" />
                  )}

                  <button
                    onClick={() =>
                      setEditingChapter(
                        editingChapter === chapter.id ? null : chapter.id
                      )
                    }
                    className="p-1.5 text-gray-400 hover:text-white hover:bg-dark-700 rounded-md transition-colors"
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>

                  <button
                    onClick={() =>
                      updateChapter(chapter.id, {
                        is_published: !chapter.is_published,
                      })
                    }
                    className={`p-1.5 rounded-md transition-colors ${
                      chapter.is_published
                        ? 'text-green-400 hover:text-green-300 hover:bg-green-500/10'
                        : 'text-gray-500 hover:text-gray-300 hover:bg-dark-700'
                    }`}
                    title={chapter.is_published ? 'Published' : 'Draft'}
                  >
                    {chapter.is_published ? (
                      <Eye className="h-4 w-4" />
                    ) : (
                      <EyeOff className="h-4 w-4" />
                    )}
                  </button>

                  <button
                    onClick={() => deleteChapter(chapter.id)}
                    className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Lessons */}
              {expandedChapters.has(chapter.id) && (
                <div className="border-t border-dark-700">
                  {chapter.lessons.length === 0 ? (
                    <div className="p-6 text-center text-gray-500 text-sm">
                      No lessons yet
                    </div>
                  ) : (
                    <div className="divide-y divide-dark-700/50">
                      {chapter.lessons.map((lesson, lessonIndex) => (
                        <div
                          key={lesson.id}
                          className="flex items-start gap-3 p-4 pl-16 hover:bg-dark-750/30 transition-colors"
                        >
                          {/* Reorder */}
                          <div className="flex flex-col gap-0.5 mt-1">
                            <button
                              onClick={() =>
                                moveLesson(chapter.id, lessonIndex, 'up')
                              }
                              disabled={lessonIndex === 0}
                              className="text-gray-500 hover:text-white disabled:opacity-20 text-xs"
                            >
                              ▲
                            </button>
                            <button
                              onClick={() =>
                                moveLesson(chapter.id, lessonIndex, 'down')
                              }
                              disabled={
                                lessonIndex === chapter.lessons.length - 1
                              }
                              className="text-gray-500 hover:text-white disabled:opacity-20 text-xs"
                            >
                              ▼
                            </button>
                          </div>

                          {/* Lesson Thumbnail */}
                          <div className="relative h-16 w-28 bg-dark-700 rounded-lg overflow-hidden flex-shrink-0 group">
                            {lesson.thumbnail_url ? (
                              <img
                                src={lesson.thumbnail_url}
                                alt=""
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center">
                                <Video className="h-5 w-5 text-gray-600" />
                              </div>
                            )}
                            <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                              {uploadingThumbnail === lesson.id ? (
                                <Loader className="h-4 w-4 animate-spin text-white" />
                              ) : (
                                <Upload className="h-4 w-4 text-white" />
                              )}
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file)
                                    handleThumbnailUpload(
                                      file,
                                      'lesson',
                                      lesson.id,
                                      chapter.id
                                    );
                                }}
                              />
                            </label>
                            {lesson.video_url && (
                              <div className="absolute bottom-1 right-1 bg-black/70 rounded px-1">
                                <Play className="h-3 w-3 text-white" />
                              </div>
                            )}
                          </div>

                          {/* Lesson Content */}
                          <div className="flex-1 min-w-0">
                            {editingLesson === lesson.id ? (
                              <div className="space-y-2">
                                <input
                                  type="text"
                                  value={lesson.title}
                                  onChange={(e) =>
                                    setChapters((prev) =>
                                      prev.map((ch) =>
                                        ch.id === chapter.id
                                          ? {
                                              ...ch,
                                              lessons: ch.lessons.map((l) =>
                                                l.id === lesson.id
                                                  ? {
                                                      ...l,
                                                      title: e.target.value,
                                                    }
                                                  : l
                                              ),
                                            }
                                          : ch
                                      )
                                    )
                                  }
                                  className="w-full bg-dark-700 text-white font-medium px-3 py-1 rounded-md border border-dark-600 focus:border-[#0000fe] focus:outline-none text-sm"
                                  autoFocus
                                />
                                <textarea
                                  value={lesson.description || ''}
                                  onChange={(e) =>
                                    setChapters((prev) =>
                                      prev.map((ch) =>
                                        ch.id === chapter.id
                                          ? {
                                              ...ch,
                                              lessons: ch.lessons.map((l) =>
                                                l.id === lesson.id
                                                  ? {
                                                      ...l,
                                                      description:
                                                        e.target.value,
                                                    }
                                                  : l
                                              ),
                                            }
                                          : ch
                                      )
                                    )
                                  }
                                  placeholder="Lesson description..."
                                  rows={2}
                                  className="w-full bg-dark-700 text-gray-300 text-sm px-3 py-1.5 rounded-md border border-dark-600 focus:border-[#0000fe] focus:outline-none resize-none"
                                />
                                <div className="flex gap-2">
                                  <input
                                    type="url"
                                    value={lesson.video_url || ''}
                                    onChange={(e) =>
                                      setChapters((prev) =>
                                        prev.map((ch) =>
                                          ch.id === chapter.id
                                            ? {
                                                ...ch,
                                                lessons: ch.lessons.map((l) =>
                                                  l.id === lesson.id
                                                    ? {
                                                        ...l,
                                                        video_url:
                                                          e.target.value || null,
                                                      }
                                                    : l
                                                ),
                                              }
                                            : ch
                                        )
                                      )
                                    }
                                    placeholder="Video URL (YouTube, Vimeo, etc.)"
                                    className="flex-1 bg-dark-700 text-gray-300 text-sm px-3 py-1.5 rounded-md border border-dark-600 focus:border-[#0000fe] focus:outline-none"
                                  />
                                  <input
                                    type="number"
                                    value={lesson.duration_seconds || ''}
                                    onChange={(e) =>
                                      setChapters((prev) =>
                                        prev.map((ch) =>
                                          ch.id === chapter.id
                                            ? {
                                                ...ch,
                                                lessons: ch.lessons.map((l) =>
                                                  l.id === lesson.id
                                                    ? {
                                                        ...l,
                                                        duration_seconds:
                                                          e.target.value
                                                            ? parseInt(
                                                                e.target.value
                                                              )
                                                            : null,
                                                      }
                                                    : l
                                                ),
                                              }
                                            : ch
                                        )
                                      )
                                    }
                                    placeholder="Duration (sec)"
                                    className="w-32 bg-dark-700 text-gray-300 text-sm px-3 py-1.5 rounded-md border border-dark-600 focus:border-[#0000fe] focus:outline-none"
                                  />
                                </div>
                                <div className="flex items-center gap-3">
                                  <button
                                    onClick={() => {
                                      updateLesson(chapter.id, lesson.id, {
                                        title: lesson.title,
                                        description: lesson.description,
                                        video_url: lesson.video_url,
                                        duration_seconds: lesson.duration_seconds,
                                      });
                                      setEditingLesson(null);
                                    }}
                                    className="flex items-center gap-1.5 px-3 py-1 text-xs bg-[#0000fe] text-white rounded-md hover:bg-[#0000dd] transition-colors"
                                  >
                                    <Save className="h-3 w-3" />
                                    Save
                                  </button>
                                  <button
                                    onClick={() => setEditingLesson(null)}
                                    className="text-xs text-gray-400 hover:text-white"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div>
                                <h4 className="font-medium text-white text-sm">
                                  <span className="text-gray-500 mr-1.5">
                                    {chapterIndex + 1}.{lessonIndex + 1}
                                  </span>
                                  {lesson.title}
                                </h4>
                                {lesson.description && (
                                  <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">
                                    {lesson.description}
                                  </p>
                                )}
                                <div className="flex items-center gap-3 mt-1">
                                  {lesson.duration_seconds && (
                                    <span className="flex items-center gap-1 text-xs text-gray-500">
                                      <Clock className="h-3 w-3" />
                                      {formatDuration(lesson.duration_seconds)}
                                    </span>
                                  )}
                                  {lesson.video_url && (
                                    <span className="flex items-center gap-1 text-xs text-green-400">
                                      <Video className="h-3 w-3" />
                                      Video linked
                                    </span>
                                  )}
                                  {lesson.is_free && (
                                    <span className="flex items-center gap-1 text-xs text-blue-400">
                                      <Unlock className="h-3 w-3" />
                                      Free
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Lesson Actions */}
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {saving === lesson.id && (
                              <Loader className="h-3.5 w-3.5 animate-spin text-gray-400" />
                            )}

                            <button
                              onClick={() =>
                                setEditingLesson(
                                  editingLesson === lesson.id
                                    ? null
                                    : lesson.id
                                )
                              }
                              className="p-1.5 text-gray-400 hover:text-white hover:bg-dark-700 rounded-md transition-colors"
                              title="Edit"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>

                            <button
                              onClick={() =>
                                updateLesson(chapter.id, lesson.id, {
                                  is_free: !lesson.is_free,
                                })
                              }
                              className={`p-1.5 rounded-md transition-colors ${
                                lesson.is_free
                                  ? 'text-blue-400 hover:text-blue-300 hover:bg-blue-500/10'
                                  : 'text-gray-500 hover:text-gray-300 hover:bg-dark-700'
                              }`}
                              title={lesson.is_free ? 'Free preview' : 'Locked'}
                            >
                              <Unlock className="h-3.5 w-3.5" />
                            </button>

                            <button
                              onClick={() =>
                                updateLesson(chapter.id, lesson.id, {
                                  is_published: !lesson.is_published,
                                })
                              }
                              className={`p-1.5 rounded-md transition-colors ${
                                lesson.is_published
                                  ? 'text-green-400 hover:text-green-300 hover:bg-green-500/10'
                                  : 'text-gray-500 hover:text-gray-300 hover:bg-dark-700'
                              }`}
                              title={
                                lesson.is_published ? 'Published' : 'Draft'
                              }
                            >
                              {lesson.is_published ? (
                                <Eye className="h-3.5 w-3.5" />
                              ) : (
                                <EyeOff className="h-3.5 w-3.5" />
                              )}
                            </button>

                            <button
                              onClick={() =>
                                deleteLesson(chapter.id, lesson.id)
                              }
                              className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add Lesson Button */}
                  <div className="p-3 pl-16">
                    <button
                      onClick={() => addLesson(chapter.id)}
                      className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                      Add Lesson
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
